import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Button,
  TextField,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import axios from 'axios';

// Status workflow
const STATUS_FLOW = ['pending', 'sent_to_machine', 'in_progress', 'completed'];
const STATUS_CONFIG = {
  'pending': { label: 'In Attesa', color: 'default', step: 0 },
  'sent_to_machine': { label: 'Inviato a Macchina', color: 'info', step: 1 },
  'in_production': { label: 'In Produzione', color: 'primary', step: 2 },
  'in_progress': { label: 'In Produzione', color: 'primary', step: 2 },
  'completed': { label: 'Completato', color: 'success', step: 3 },
  'error': { label: 'Errore', color: 'error', step: -1 },
  'paused': { label: 'In Pausa', color: 'warning', step: -1 },
  'stopped': { label: 'Fermato dalla Macchina', color: 'warning', step: -1 },
  'cancelled': { label: 'Annullato', color: 'error', step: -1 }
};

// Bionen reject type descriptions
const REJECT_DESCRIPTIONS = {
  'scarti_00': 'Mancanza ago (ST3)',
  'scarti_01': 'Diametro non corretto (ST3)',
  'scarti_02': 'Lunghezza ago non corretta (ST3)',
  'scarti_03': 'Quota verifica non raggiungibile (ST3)',
  'scarti_04': 'Verifica taglio fallita (ST3)',
  'scarti_05': 'Mancanza ago (ST2)',
  'scarti_06': 'Riservato',
  'scarti_07': 'Riservato',
  'scarti_08': 'Riservato',
  'scarti_09': 'Riservato',
  'scarti_10': 'Riservato'
};

// OPC UA node mapping for Bionen orders
const OPCUA_MAPPING = [
  { field: 'guid', label: 'GUID Ordine', node: 'DatiOrdine_GUID', direction: 'write', area: 'DatiOrdine' },
  { field: 'commessa', label: 'Commessa / Ordine', node: 'DatiOrdine_Ordine', direction: 'write', area: 'DatiOrdine' },
  { field: 'product_code', label: 'Codice Prodotto', node: 'DatiOrdine_Prodotto', direction: 'write', area: 'DatiOrdine' },
  { field: 'customer_name', label: 'Cliente', node: 'DatiOrdine_Cliente', direction: 'write', area: 'DatiOrdine' },
  { field: 'notes', label: 'Note', node: 'DatiOrdine_Note', direction: 'write', area: 'DatiOrdine' },
  { field: 'quantity', label: 'Quantita', node: 'DatiOrdine_Qt', direction: 'write', area: 'DatiOrdine' },
  { field: null, label: 'Dati Pronti', node: 'DatiOrdinePronti', direction: 'write', area: 'DatiOrdine', auto: true },
  { field: 'produced_quantity', label: 'Pezzi Buoni (PLC)', node: 'PezziBuoni', direction: 'read', area: 'Lavorazione' },
  { field: null, label: 'Produzione In Corso', node: 'ProduzioneInCorso', direction: 'read', area: 'Lavorazione' },
  { field: 'good_pieces', label: 'Pezzi Buoni (Consuntivo)', node: 'FineLavorazione_PezziBuoni', direction: 'read', area: 'FineLavorazione' },
  { field: 'production_start_time', label: 'Inizio Produzione', node: 'FineLavorazione_StartTime', direction: 'read', area: 'FineLavorazione' },
  { field: 'production_end_time', label: 'Fine Produzione', node: 'FineLavorazione_EndTime', direction: 'read', area: 'FineLavorazione' },
];

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });

  const loadOrder = useCallback(async () => {
    try {
      const res = await axios.get(`/api/orders/${id}`);
      setOrder(res.data);
      setEditData(res.data);
    } catch (err) {
      console.error('Error loading order:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadLookups = useCallback(async () => {
    try {
      const [machRes, custRes, prodRes] = await Promise.all([
        axios.get('/api/machines'),
        axios.get('/api/customers'),
        axios.get('/api/products')
      ]);
      setMachines(machRes.data);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      console.error('Error loading lookups:', err);
    }
  }, []);

  useEffect(() => {
    loadOrder();
    loadLookups();
  }, [loadOrder, loadLookups]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSave = async () => {
    try {
      await axios.put(`/api/orders/${id}`, {
        product_code: editData.product_code,
        quantity: editData.quantity,
        customer_id: editData.customer_id,
        reference: editData.reference,
        status: editData.status
      });
      showSnackbar('Ordine aggiornato', 'success');
      setEditing(false);
      loadOrder();
    } catch (err) {
      showSnackbar('Errore nel salvataggio', 'error');
    }
  };

  const handleSendToProduction = async () => {
    try {
      await axios.post(`/api/orders/${id}/send-to-production`);
      showSnackbar('Ordine inviato alla produzione', 'success');
      loadOrder();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Errore nell\'invio', 'error');
    }
    setConfirmDialog({ open: false });
  };

  const handleStopProduction = async () => {
    try {
      const res = await axios.post(`/api/orders/${id}/stop-production`);
      if (res.data?.status === 'withdraw_requested') {
        showSnackbar('Ritiro richiesto alla macchina: lo stato si aggiornera\' a conferma del gateway', 'info');
      } else {
        showSnackbar('Ordine ritirato', 'success');
      }
      loadOrder();
    } catch (err) {
      // 409 = ordine in lavorazione sulla macchina: non ritirabile da remoto (fermare da HMI)
      const msg = err.response?.data?.error || 'Errore nel ritiro dell\'ordine';
      showSnackbar(msg, err.response?.status === 409 ? 'warning' : 'error');
    }
    setConfirmDialog({ open: false });
  };

  const handleForceCancel = async () => {
    try {
      const res = await axios.post(`/api/orders/${id}/force-cancel`);
      showSnackbar(res.data?._message || 'Ordine annullato forzatamente', 'success');
      loadOrder();
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Errore nell\'annullamento forzato', 'error');
    }
    setConfirmDialog({ open: false });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'send') return handleSendToProduction();
    if (confirmDialog.action === 'force_cancel') return handleForceCancel();
    return handleStopProduction();
  };

  const canEdit = order?.status === 'pending';
  const canSend = order?.status === 'pending' && order?.machine_id;
  // Ritirabile da remoto SOLO se non in lavorazione sulla macchina.
  const canStop = ['sent_to_machine', 'stopped', 'error'].includes(order?.status);
  // Annullamento forzato (anche operatore normale): per ordini appesi che bloccano la macchina.
  const canForceCancel = ['sent_to_machine', 'in_progress', 'in_production', 'error', 'stopped'].includes(order?.status);
  const isInProduction = ['in_production', 'in_progress'].includes(order?.status);
  const isCompleted = order?.status === 'completed';
  const statusInfo = STATUS_CONFIG[order?.status] || STATUS_CONFIG['pending'];
  const activeStep = statusInfo.step >= 0 ? statusInfo.step : -1;

  const machineName = machines.find(m => m.id === order?.machine_id)?.name || '-';
  const customerName = order?.customer_name || customers.find(c => c.id === order?.customer_id)?.name || '-';

  const formatDate = (d) => d ? new Date(d).toLocaleString('it-IT') : '-';

  // Totale scarti (somma dei contatori Scarti_00..10 del consuntivo Bionen)
  const totalRejects = order?.rejects
    ? Object.values(order.rejects).reduce((sum, r) => sum + (Number(r?.count) || 0), 0)
    : 0;

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  if (!order) {
    return <Alert severity="error">Ordine non trovato</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate('/orders')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            Ordine #{order.id}
            {order.commessa && <Typography component="span" variant="h5" color="text.secondary"> - {order.commessa}</Typography>}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {order.product_code} {order.product_description ? `- ${order.product_description}` : ''}
          </Typography>
        </Box>
        <Chip label={statusInfo.label} color={statusInfo.color} />
        {canEdit && !editing && (
          <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEditing(true)}>
            Modifica
          </Button>
        )}
        {canSend && (
          <Button
            startIcon={<SendIcon />}
            variant="contained"
            color="secondary"
            onClick={() => setConfirmDialog({
              open: true,
              action: 'send',
              title: 'Inviare alla produzione?',
              message: `L'ordine #${order.id} (${order.product_code}, ${order.quantity} pz) verra' inviato alla macchina ${machineName}. Un GUID verra' generato e i dati scritti sui nodi OPC UA DatiOrdine.`
            })}
          >
            Invia a Produzione
          </Button>
        )}
        {canStop && (
          <Button
            startIcon={<StopIcon />}
            variant="outlined"
            color="error"
            onClick={() => setConfirmDialog({
              open: true,
              action: 'stop',
              title: 'Ritirare l\'ordine?',
              message: order?.status === 'sent_to_machine'
                ? 'La proposta verra\' ritirata dalla macchina (se non ancora presa in carico) e l\'ordine riportato in stato "In Attesa".'
                : 'L\'ordine verra\' riportato in stato "In Attesa".'
            })}
          >
            Ritira Ordine
          </Button>
        )}
        {isInProduction && (
          <Chip
            icon={<StopIcon />}
            color="warning"
            variant="outlined"
            label="In lavorazione: fermare da HMI"
            title="L'ordine e' gia' preso in carico dalla macchina e non e' ritirabile da remoto. Va fermato dall'operatore sulla macchina (HMI); poi sara' di nuovo ritirabile."
          />
        )}
        {canForceCancel && (
          <Button
            startIcon={<CancelIcon />}
            variant="contained"
            color="error"
            onClick={() => setConfirmDialog({
              open: true,
              action: 'force_cancel',
              title: 'Annullare forzatamente l\'ordine?',
              message: `L'ordine #${order.id} verra' impostato ad "Annullato" e verra' richiesto al gateway di sbloccare la macchina ${machineName} (protocollo Bionen a IDLE). ATTENZIONE: se la macchina sta davvero lavorando l'ordine, il lavoro prosegue e va fermato da HMI; questo sblocca solo il tracciamento cloud/gateway.`
            })}
          >
            Annulla Forzatamente
          </Button>
        )}
        <IconButton onClick={loadOrder} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Status Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {['In Attesa', 'Inviato a Macchina', 'In Produzione', 'Completato'].map((label, idx) => (
            <Step key={label} completed={activeStep > idx}>
              <StepLabel error={order.status === 'error' && idx === activeStep}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Grid container spacing={3}>
        {/* Left column: Order Info */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Dettagli Ordine</Typography>

            {editing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Codice Prodotto" value={editData.product_code || ''} onChange={(e) => setEditData({ ...editData, product_code: e.target.value })} size="small" />
                <TextField label="Quantita'" type="number" value={editData.quantity || ''} onChange={(e) => setEditData({ ...editData, quantity: parseInt(e.target.value) || 0 })} size="small" />
                <TextField select label="Cliente" value={editData.customer_id || ''} onChange={(e) => setEditData({ ...editData, customer_id: e.target.value })} size="small">
                  <MenuItem value="">Nessuno</MenuItem>
                  {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <TextField select label="Macchina" value={editData.machine_id || ''} onChange={(e) => setEditData({ ...editData, machine_id: e.target.value })} size="small">
                  <MenuItem value="">Nessuna</MenuItem>
                  {machines.filter(m => m.is_active).map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </TextField>
                <TextField label="Commessa" value={editData.commessa || ''} onChange={(e) => setEditData({ ...editData, commessa: e.target.value })} size="small" />
                <TextField label="Riferimento" value={editData.reference || ''} onChange={(e) => setEditData({ ...editData, reference: e.target.value })} size="small" />
                <TextField label="Note" value={editData.notes || ''} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} size="small" multiline rows={2} />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button startIcon={<CancelIcon />} onClick={() => { setEditing(false); setEditData(order); }}>Annulla</Button>
                  <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSave}>Salva</Button>
                </Box>
              </Box>
            ) : (
              <Table size="small">
                <TableBody>
                  {[
                    ['Codice Prodotto', order.product_code],
                    ['Descrizione', order.product_description || '-'],
                    ['Quantita\'', `${order.quantity} pz`],
                    ['Commessa', order.commessa || '-'],
                    ['Riferimento', order.reference || '-'],
                    ['Cliente', customerName],
                    ['Macchina', machineName],
                    ['Note', order.notes || '-'],
                    ['GUID', order.guid || 'Non ancora generato'],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ fontWeight: 600, width: '40%', borderBottom: '1px solid #f0f0f0' }}>{label}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #f0f0f0', fontFamily: label === 'GUID' ? 'monospace' : 'inherit' }}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>

        {/* Right column: Timeline + Production data */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Timeline</Typography>
            <Table size="small">
              <TableBody>
                {[
                  ['Creato', formatDate(order.created_at)],
                  ['Inviato a Edge', formatDate(order.sent_to_edge_at)],
                  ['Invio Macchina', formatDate(order.send_date)],
                  ['Inizio Produzione', formatDate(order.started_production_at || order.production_start_time)],
                  ['Fine Produzione', formatDate(order.completed_at || order.production_end_time)],
                ].map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell sx={{ fontWeight: 600, width: '45%', borderBottom: '1px solid #f0f0f0' }}>{label}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* Production Results (visible after production) */}
          {(order.status !== 'pending') && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Dati Produzione</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" color="text.secondary">Prodotti</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        {order.produced_quantity || order.good_pieces || 0}
                      </Typography>
                      <Typography variant="caption">/ {order.quantity}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" color="text.secondary">Pezzi Buoni</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {order.good_pieces || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ borderColor: totalRejects > 0 ? 'error.light' : undefined }}>
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" color="text.secondary">Scarti</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: totalRejects > 0 ? 'error.main' : 'text.secondary' }}>
                        {totalRejects}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" color="text.secondary">Tempo Ciclo</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {order.cycle_time_ms ? `${order.cycle_time_ms} ms` : '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Reject breakdown - sempre visibile in produzione/completato */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Scarti per Tipologia</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descrizione</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Quantita'</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(order.rejects || {})
                    .filter(([, data]) => data?.count > 0)
                    .map(([key, data]) => (
                      <TableRow key={key}>
                        <TableCell>{key}</TableCell>
                        <TableCell>{data.description || REJECT_DESCRIPTIONS[key] || '-'}</TableCell>
                        <TableCell align="right">{data.count}</TableCell>
                      </TableRow>
                    ))}
                  {totalRejects === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nessuno scarto registrato
                          {isInProduction ? ' (il dettaglio arriva col consuntivo a fine lavorazione)' : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* OPC UA Node Mapping */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountTreeIcon color="secondary" />
          <Typography variant="h6">Mappatura OPC UA</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Corrispondenza tra i campi dell'ordine e i nodi OPC UA sulla macchina (protocollo Bionen).
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Campo Ordine</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nodo OPC UA</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Direzione</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Valore Attuale</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {OPCUA_MAPPING.map((m, idx) => {
                let currentValue = '-';
                if (m.field && order[m.field] !== null && order[m.field] !== undefined) {
                  currentValue = String(order[m.field]);
                }
                if (m.auto) currentValue = '(automatico)';

                return (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Chip
                        label={m.area}
                        size="small"
                        variant="outlined"
                        color={m.area === 'DatiOrdine' ? 'info' : m.area === 'Lavorazione' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{m.label}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{m.node}</TableCell>
                    <TableCell>
                      <Chip
                        label={m.direction === 'write' ? 'Gestionale > Macchina' : 'Macchina > Gestionale'}
                        size="small"
                        color={m.direction === 'write' ? 'info' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{currentValue}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false })}>Annulla</Button>
          <Button
            variant="contained"
            color={['stop', 'force_cancel'].includes(confirmDialog.action) ? 'error' : 'primary'}
            onClick={handleConfirmAction}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetail;
